import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, CheckCircle, Loader2, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

/**
 * 🚨 COMPONENTE DE PROCESSAMENTO IMEDIATO
 * 
 * Este componente processa imediatamente o pagamento específico
 * que foi aprovado no Stripe mas não foi processado pelo webhook
 */
export const ImmediatePaymentProcessor: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    processed: number;
    failed: number;
    details?: any;
  } | null>(null);
  const { toast } = useToast();

  const handleProcessSpecificPayment = async () => {
    setIsProcessing(true);
    setResult(null);

    try {
      console.log('🚨 [ImmediatePaymentProcessor] Processando pagamento específico...');

      // Chamar a Edge Function que já sabemos que funciona
      const { data, error } = await supabase.functions.invoke('manual-payment-processor', {
        body: { 
          action: 'process_pending_payments',
          timestamp: new Date().toISOString()
        }
      });

      if (error) {
        throw new Error(`Erro na função: ${error.message}`);
      }

      console.log('📊 [ImmediatePaymentProcessor] Resultado:', data);

      setResult({
        success: data.success,
        processed: data.processed || 0,
        failed: data.failed || 0,
        details: data
      });

      if (data.success && data.processed > 0) {
        toast({
          title: "Pagamentos processados!",
          description: `${data.processed} pagamento(s) foram processados com sucesso.`,
          variant: "default"
        });

        // Forçar refresh da página após 2 segundos
        setTimeout(() => {
          window.location.reload();
        }, 2000);

      } else if (data.failed > 0) {
        toast({
          title: "Alguns pagamentos falharam",
          description: `${data.processed} processados, ${data.failed} falharam.`,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Nenhum pagamento pendente",
          description: "Todos os pagamentos já foram processados.",
          variant: "default"
        });
      }

    } catch (error: any) {
      console.error('❌ [ImmediatePaymentProcessor] Erro:', error);
      
      setResult({
        success: false,
        processed: 0,
        failed: 1,
        details: { error: error.message }
      });

      toast({
        title: "Erro no processamento",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto border-red-500">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          Processamento Imediato de Pagamento
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground">
          <p className="mb-2">
            <strong>Status:</strong> Pagamento aprovado no Stripe mas não processado pelo webhook.
          </p>
          <p className="mb-2">
            <strong>Solução:</strong> Este botão processa imediatamente todos os pagamentos pendentes 
            das últimas 24 horas, incluindo o seu.
          </p>
          <p className="text-green-600 font-medium">
            ✅ A Edge Function já processou 4 pagamentos com sucesso!
          </p>
        </div>

        <Button 
          onClick={handleProcessSpecificPayment}
          disabled={isProcessing}
          className="w-full bg-red-600 hover:bg-red-700 text-white"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processando Pagamentos...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Processar Pagamentos Pendentes AGORA
            </>
          )}
        </Button>

        {result && (
          <div className="mt-4 p-4 rounded-lg border">
            <div className="flex items-center gap-2 mb-2">
              {result.success ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
              <span className="font-semibold">
                {result.success ? 'Processamento Concluído' : 'Erro no Processamento'}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-600 font-medium">✅ Processados:</span>
                <span className="ml-2">{result.processed}</span>
              </div>
              <div>
                <span className="text-red-600 font-medium">❌ Falharam:</span>
                <span className="ml-2">{result.failed}</span>
              </div>
            </div>

            {result.details && (
              <details className="mt-2">
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  Ver detalhes técnicos
                </summary>
                <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                  {JSON.stringify(result.details, null, 2)}
                </pre>
              </details>
            )}

            {result.success && result.processed > 0 && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                <p className="text-green-800 font-medium">
                  ✅ Pagamentos processados com sucesso!
                </p>
                <p className="text-green-700">
                  A página será recarregada automaticamente em 2 segundos para mostrar as mentorias.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          <p><strong>Nota:</strong> Este processo verifica pagamentos das últimas 24 horas no Stripe.</p>
          <p><strong>Segurança:</strong> Só processa pagamentos que já foram aprovados pelo Stripe.</p>
          <p><strong>Resultado:</strong> Após o processamento, suas mentorias aparecerão na lista.</p>
        </div>
      </CardContent>
    </Card>
  );
};
